export interface LocalApkInfo {
  file: File;
  path: string;
  lastModified: number;
  size: number;
}

const IDB_NAME = 'yumacall-local-apk';
const IDB_STORE = 'handles';
/**
 * La chiave con cui si ricorda la cartella scelta, quando chi chiama non ne indica una.
 *
 * ⚠️ ERA L'UNICA CHIAVE ESISTENTE, ed è metà del difetto che faceva confondere le app fra loro:
 * tutte e tre condividevano la stessa cartella ricordata, cioè l'ultima scelta. La pagina
 * passava già una chiave per app, ma si fermava al componente e non arrivava fin qui.
 */
const IDB_KEY_PREDEFINITA = 'yumacall-project-dir';
const IDB_VERSION = 1;

const APK_SEARCH_DIRS = [
  // ⚠️ QUESTA VIENE PER PRIMA. YumaWORK e YumaFLY, a fine compilazione, COPIANO l'APK qui:
  // `build/outputs/` è cartella di Gradle e portargli via un file da lì dentro gli fa perdere
  // il conto di cosa ha già prodotto (esce un APK troncato che il telefono rifiuta). Cercare
  // solo dentro `outputs` voleva dire non trovare mai l'APK di quelle due app.
  ['app', 'build', 'apk-da-caricare'],
  ['app', 'build', 'outputs', 'apk', 'release'],
  ['app', 'build', 'outputs', 'apk', 'debug'],
];

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function isFileSystemAccessSupported(): boolean {
  return 'showDirectoryPicker' in window;
}

export async function saveDirectoryHandle(
  handle: FileSystemDirectoryHandle,
  chiave: string = IDB_KEY_PREDEFINITA,
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(handle, chiave);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getDirectoryHandle(
  chiave: string = IDB_KEY_PREDEFINITA,
): Promise<FileSystemDirectoryHandle | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(chiave);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

export async function clearDirectoryHandle(
  chiave: string = IDB_KEY_PREDEFINITA,
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).delete(chiave);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function requestPermission(handle: FileSystemDirectoryHandle): Promise<boolean> {
  const opts = { mode: 'read' as const };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const h = handle as any;
  let state: PermissionState = await h.queryPermission(opts);
  if (state === 'granted') return true;
  state = await h.requestPermission(opts);
  return state === 'granted';
}

export async function scanForApk(
  dirHandle: FileSystemDirectoryHandle,
  /**
   * Come si chiama l'APK dell'app che si sta pubblicando.
   *
   * ⚠️ PRIMA ERA CABLATO A `yumacall.apk`, ed è il difetto che faceva confondere le app fra
   * loro: puntando la cartella di YumaWORK non trovava niente, e puntando quella di YumaCALL
   * mentre era selezionata un'altra app trovava l'APK di YumaCALL e si offriva di pubblicarlo
   * sotto l'altro nome. Il nome del file lo decide chi ha scelto l'app, non questa funzione.
   */
  apkFileName: string,
): Promise<LocalApkInfo | null> {
  for (const segments of APK_SEARCH_DIRS) {
    try {
      let current: FileSystemDirectoryHandle = dirHandle;
      for (const seg of segments) {
        current = await current.getDirectoryHandle(seg);
      }
      const fileHandle = await current.getFileHandle(apkFileName);
      const file: File = await fileHandle.getFile();
      const path = [...segments, apkFileName].join('/');
      return { file, path, lastModified: file.lastModified, size: file.size };
    } catch {
      continue;
    }
  }
  return null;
}
